import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import classNames from 'classnames';
import { Contact } from 'models/Contact';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import { Button } from 'ncoded-component-library';
import { Field, Form } from 'react-final-form';
import { BasicUserInfo } from 'models/User';
import { FormApi } from 'final-form';
import { useTranslation } from 'react-i18next';
import WarningExclamationIcon from 'icons/WarningExclamation.icon';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import InputField from 'components/InputField';
import useValidations from 'router/subrouters/Auth/hooks/useValidations';
import PhoneFieldWrapper from 'components/PhoneFieldWrapper';
import api from 'api';
import TrashIcon from 'icons/Trash.icon';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import useUserBasicInfo from 'hooks/useUserBasicInfo';
import EmailField from 'components/EmailField';
import TextAreaField from 'components/TextAreaField';

import './ManageContactDrawer.styles.scss';

const FIELDS_TO_UPDATE: (keyof BasicUserInfo)[] = [
  'firstName',
  'lastName',
  'phone',
  'title',
  'companyName',
];

type ManageContactDrawerProps = {
  className?: string;
  contact?: Partial<Contact>;
  onSuccess?: (contact: Contact) => void;
  onDelete?: (id: string) => Promise<void>;
};

const ManageContactDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  ManageContactDrawerProps
> = (props, ref) => {
  const { className, contact, onSuccess, onDelete } = props;

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);

  const classes = classNames('manage-contact-drawer', className);

  const formRef = useRef<FormApi<Contact, Partial<Contact>> | null>(null);

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const commonValidations = useValidations();

  const {
    isFetchingUserBasicInfo,
    isUserBasicInfoSet,
    prefilledFields,
    onEmailChange,
    resetState,
  } = useUserBasicInfo();

  const isAddingNewContact = !contact;
  const isEditingContact = !!contact;

  const {
    firstName,
    lastName,
    email,
    companyName,
    title,
    phone,
    preApproved,
    existUser,
  } = contact || {};

  const disabledEmail = isEditingContact;

  const hasResolveAccount = isUserBasicInfoSet || existUser;

  const handleOnClose = useCallback(() => {
    drawer?.setOpen(false);
    resetState();
    formRef.current?.restart();
    formRef.current?.reset();
  }, [drawer, resetState]);

  const { t } = useTranslation();

  const initialValues = useMemo(
    () =>
      !contact
        ? null
        : {
            firstName,
            lastName,
            email,
            companyName,
            title,
            phone: phone || undefined,
            preApproved: preApproved || false,
          },
    [
      companyName,
      contact,
      email,
      firstName,
      lastName,
      phone,
      preApproved,
      title,
    ],
  );

  const onSubmit = useCallback(
    async (values: Contact & { invitationMessage?: string }) => {
      try {
        const {
          firstName,
          lastName,
          phone,
          preApproved,
          companyName,
          title,
          invitationMessage,
        } = values;

        let contactData;
        let hasInvited;

        if (isAddingNewContact) {
          const {
            data: { contact, hasInvited: hasInvitedResponse },
          } = await api.contacts.createContact({
            ...values,
            invitationMessage: (invitationMessage || '').trim() || undefined,
          });

          contactData = contact;
          hasInvited = hasInvitedResponse;
        } else {
          const { data } = await api.contacts.updateContact(contact._id, {
            firstName,
            lastName,
            preApproved,
            companyName: companyName || null,
            title: title || null,
            ...(phone && {
              phone: { ...phone, phoneNumber: phone.phoneNumber || null },
            }),
          });

          contactData = data;
        }

        popSuccess(
          t(
            hasInvited
              ? 'contactInvited'
              : isAddingNewContact
              ? 'contactCreated'
              : 'contactUpdated',
            { count: 1 },
          ),
        );

        onSuccess?.(contactData);
        handleOnClose();
      } catch (e) {
        popServerError(e);
      }
    },
    [
      contact,
      isAddingNewContact,
      popSuccess,
      t,
      onSuccess,
      handleOnClose,
      popServerError,
    ],
  );

  return (
    <SideDrawer ref={drawerRef} className={classes} closeAction={handleOnClose}>
      {drawer?.open && (
        <Form
          initialValues={initialValues}
          onSubmit={onSubmit}
          render={({ submitting, invalid, pristine, form, handleSubmit }) => {
            formRef.current = form;

            return (
              <form onSubmit={handleSubmit}>
                <p>{t(isAddingNewContact ? 'addAContact' : 'editAContact')}</p>

                {hasResolveAccount && (
                  <p className="warning">
                    <WarningExclamationIcon />
                    {t(
                      isAddingNewContact
                        ? 'thisUserAlreadyHasAccount'
                        : 'thisContactHasEditedDetails',
                    )}
                  </p>
                )}

                <div>
                  <Field
                    name="firstName"
                    label={t('firstName')}
                    component={InputField}
                    required
                    disabled={prefilledFields.firstName || existUser}
                    validate={commonValidations.name}
                  />
                  <Field
                    name="lastName"
                    label={t('lastName')}
                    component={InputField}
                    required
                    disabled={prefilledFields.lastName || existUser}
                    validate={commonValidations.name}
                  />
                </div>

                <EmailField
                  required
                  disabled={disabledEmail}
                  onChangeHandle={(email) =>
                    onEmailChange({
                      email,
                      form,
                      fieldsToPrefill: FIELDS_TO_UPDATE,
                    })
                  }
                />

                <PhoneFieldWrapper
                  disabled={prefilledFields.phone || existUser}
                />

                <Field
                  name="companyName"
                  disabled={prefilledFields.companyName || existUser}
                  label={t('company')}
                  component={InputField}
                />

                <Field
                  name="title"
                  disabled={prefilledFields.title || existUser}
                  label={t('professionalTitle')}
                  component={InputField}
                />

                <Field
                  name="preApproved"
                  label={t('preApprovedDescription')}
                  type="checkbox"
                  component={CheckboxField}
                />

                {isAddingNewContact && (
                  <Field
                    name="invitationMessage"
                    component={TextAreaField}
                    rows={6}
                    description={t('broadcastMessageEmailDescription')}
                  />
                )}

                <div className="actions">
                  {onDelete && (
                    <Button
                      type="button"
                      variant="link"
                      className="warning-action-button"
                      icon={TrashIcon}
                      onClick={async () => {
                        await onDelete(contact?._id);
                        handleOnClose();
                      }}
                    >
                      {t('delete')}
                    </Button>
                  )}

                  <Button
                    type="submit"
                    disabled={
                      submitting ||
                      invalid ||
                      pristine ||
                      isFetchingUserBasicInfo
                    }
                  >
                    {t(isAddingNewContact ? 'addContact' : 'updateContact')}
                  </Button>
                </div>
              </form>
            );
          }}
        />
      )}
    </SideDrawer>
  );
};

export default forwardRef(ManageContactDrawer);
